import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSerializeCallback } from "@kalodata/tools";
import { queryAccountProductList } from "@apis/http/api";
import type { AccountProduct } from "./types";

const PAGE_SIZE = 20;

export interface UseAccountProductsResult {
  products: AccountProduct[];
  /** 首屏加载中（products 仍为空） */
  loading: boolean;
  /** 后续分页加载中 */
  loadingMore: boolean;
  /** 是否还有下一页（首屏未加载前默认 true，避免抢跑判定） */
  hasMore: boolean;
  /** 首次打开时调用；已加载过则 noop */
  loadInitial: () => Promise<void>;
  /** 滚动到底部时调用；无下一页 / 加载中则 noop */
  loadMore: () => Promise<void>;
  /** 仅在已加载页内做大小写不敏感包含匹配 */
  filter: (keyword: string) => AccountProduct[];
}

/** 共享商品列表 hook：linkId 维度下基于 pageToken 游标分页累积，关键词在客户端做大小写不敏感包含匹配 */
export const useAccountProducts = (linkId: number): UseAccountProductsResult => {
  const [products, setProducts] = useState<AccountProduct[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);

  const initializedRef = useRef(false);
  const nextPageTokenRef = useRef<string | undefined>(undefined);
  const linkIdRef = useRef(linkId);

  useEffect(() => {
    linkIdRef.current = linkId;
  }, [linkId]);

  const loadPage = useSerializeCallback(async () => {
    const requestLinkId = linkId;
    const token = nextPageTokenRef.current;
    const isFirst = !initializedRef.current;
    if (!isFirst && !token) return;

    const res = await queryAccountProductList({
      linkId: requestLinkId,
      pageSize: PAGE_SIZE,
      ...(token ? { pageToken: token } : {}),
    });

    if (linkIdRef.current !== requestLinkId) return;

    const list = res?.products ?? [];
    const next = res?.nextPageToken || undefined;
    nextPageTokenRef.current = next;
    setNextPageToken(next);
    setProducts((prev) => (isFirst ? list : [...prev, ...list]));
    if (isFirst) {
      initializedRef.current = true;
      setInitialized(true);
    }
  });

  useEffect(() => {
    initializedRef.current = false;
    nextPageTokenRef.current = undefined;
    setInitialized(false);
    setNextPageToken(undefined);
    setProducts([]);
  }, [linkId]);

  const loadInitial = useCallback(async () => {
    if (initializedRef.current) return;
    await loadPage();
  }, [loadPage]);

  const loadMore = useCallback(async () => {
    if (!initializedRef.current) return;
    if (!nextPageTokenRef.current) return;
    await loadPage();
  }, [loadPage]);

  const filter = useMemo(
    () => (keyword: string) => {
      const k = keyword.trim().toLowerCase();
      if (!k) return products;
      return products.filter((p) => (p.productTitle ?? "").toLowerCase().includes(k));
    },
    [products],
  );

  const pending = loadPage.isPending();

  return {
    products,
    loading: pending && !initialized,
    loadingMore: pending && initialized,
    hasMore: !initialized || Boolean(nextPageToken),
    loadInitial,
    loadMore,
    filter,
  };
};
